import React from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Calendar, Clock, Users, Mail, Hash, Download } from 'lucide-react';
import { ReservationResult } from './types/reservation.types';

interface ReservationSuccessProps {
  result: ReservationResult;
  onClose: () => void;
}

const checkVariants = {
  hidden: { pathLength: 0, opacity: 0 },
  visible: { pathLength: 1, opacity: 1, transition: { duration: 0.6, ease: 'easeOut' as const } },
};

const circleVariants = {
  hidden: { pathLength: 0 },
  visible: { pathLength: 1, transition: { duration: 0.5, ease: 'easeOut' as const } },
};

function generateICS(result: ReservationResult): string {
  // Parse the reservation date from the raw ISO date string
  const rawDate = result.rawDate; // YYYY-MM-DD
  const [hours, minutes] = result.time.split(':').map(Number);
  
  // Build DTSTART in local time format (no Z suffix = floating time)
  const dateClean = rawDate.replace(/-/g, '');
  const dtStart = `${dateClean}T${String(hours).padStart(2, '0')}${String(minutes).padStart(2, '0')}00`;
  
  // End time = start + 2h default
  const endHours = hours + 2;
  const dtEnd = `${dateClean}T${String(endHours).padStart(2, '0')}${String(minutes).padStart(2, '0')}00`;

  return [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//MiamFood//Reservation//FR',
    'BEGIN:VEVENT',
    `DTSTART:${dtStart}`,
    `DTEND:${dtEnd}`,
    `SUMMARY:Réservation - ${result.restaurantName}`,
    `LOCATION:${result.restaurantAddress}`,
    `DESCRIPTION:Réservation pour ${result.partySize} personnes à ${result.time}. Réf: ${result.referenceCode}`,
    'END:VEVENT',
    'END:VCALENDAR',
  ].join('\r\n');
}

const ReservationSuccess: React.FC<ReservationSuccessProps> = ({ result, onClose }) => {
  const handleDownloadICS = () => {
    const ics = generateICS(result);
    const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `reservation-${result.referenceCode}.ics`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.4, ease: 'easeOut' }}
      className="flex flex-col items-center gap-6 py-4 px-2"
    >
      {/* Animated checkmark */}
      <div className="relative">
        <svg width="80" height="80" viewBox="0 0 80 80">
          <motion.circle
            cx="40" cy="40" r="36"
            fill="none"
            strokeWidth="3"
            className="stroke-primary"
            variants={circleVariants}
            initial="hidden"
            animate="visible"
          />
          <motion.path
            d="M24 42 L34 52 L56 30"
            fill="none"
            strokeWidth="3.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="stroke-primary"
            variants={checkVariants}
            initial="hidden"
            animate="visible"
          />
        </svg>
      </div>

      <div className="text-center">
        <h2 className="text-xl font-bold text-foreground">
          {result.requiresEmailConfirmation
            ? 'Réservation enregistrée !'
            : 'Réservation confirmée !'}
        </h2>
        <p className="text-sm text-muted-foreground mt-1">
          {result.requiresEmailConfirmation
            ? 'Vérifiez votre email pour confirmer'
            : 'Vous recevrez un email de confirmation'}
        </p>
      </div>

      <Card className="w-full max-w-sm">
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center gap-3">
            <Calendar className="h-4 w-4 text-primary flex-shrink-0" />
            <span className="text-sm capitalize">{result.date}</span>
          </div>
          <div className="flex items-center gap-3">
            <Clock className="h-4 w-4 text-primary flex-shrink-0" />
            <span className="text-sm">{result.time}</span>
          </div>
          <div className="flex items-center gap-3">
            <Users className="h-4 w-4 text-primary flex-shrink-0" />
            <span className="text-sm">{result.partySize} personne{result.partySize > 1 ? 's' : ''}</span>
          </div>
          <div className="flex items-center gap-3">
            <Hash className="h-4 w-4 text-primary flex-shrink-0" />
            <span className="text-sm font-mono font-bold">{result.referenceCode}</span>
          </div>
          <div className="flex items-center gap-3">
            <Mail className="h-4 w-4 text-primary flex-shrink-0" />
            <span className="text-sm text-muted-foreground">{result.customerEmail}</span>
          </div>
        </CardContent>
      </Card>

      <div className="flex flex-col gap-2 w-full max-w-sm">
        <Button variant="outline" onClick={handleDownloadICS} className="w-full gap-2">
          <Download className="h-4 w-4" />
          Ajouter à mon calendrier
        </Button>
        <Button onClick={onClose} className="w-full">
          Fermer
        </Button>
      </div>

      <p className="text-xs text-muted-foreground text-center px-4">
        Consultez votre email de confirmation pour modifier ou annuler votre réservation.
      </p>
    </motion.div>
  );
};

export default ReservationSuccess;
